import { Injectable } from '@angular/core';
import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    AccountType,
    IAccount,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { AccountsHelper } from '../../../../aumico-project-reports/src/app/microservices/ms-annual-reports/helpers/accounts.helper';

@Injectable({ providedIn: 'root' })
export class AccountMapper {
    /**
     * Returns all accounts that belong to the equity group of the balance template,
     * filtered to those with at least one balance value.
     */
    getAccountsOfEquityGroup(balanceTemplate: IAccountMappingTemplate): IAccount[] {
        if (!balanceTemplate?.equityGroupName) {
            return [];
        }
        const equityGroup = this.findGroupByName([balanceTemplate.group], balanceTemplate.equityGroupName);
        if (!equityGroup) {
            return [];
        }
        // Create a synthetic template rooted at the equity group to reuse getAccountsOfTemplate
        const equityTemplate: IAccountMappingTemplate = { ...balanceTemplate, group: equityGroup };
        const accounts = this.getAccountsOfTemplate(equityTemplate);
        return AccountsHelper.roundAccountsValues(
            accounts.filter(
                (account) => account.calculatedOpeningBalance != null || account.calculatedClosingBalance != null,
            ),
        );
    }

    getMappedAccountsOfBalanceTemplate(balanceTemplate: IAccountMappingTemplate): IAccount[] {
        let mappedAccounts: IAccount[] = [];
        let filteredMappedAccounts: IAccount[] = [];

        if (balanceTemplate == null) {
            return mappedAccounts;
        }

        mappedAccounts = this.getAccountsOfTemplate(balanceTemplate);
        // this is to make transferred profit account not appear in the aop
        filteredMappedAccounts = mappedAccounts.filter((account) => account.type === AccountType.BALANCE);

        return AccountsHelper.roundAccountsValues(filteredMappedAccounts);
    }

    getAccountsOfTemplate(template: IAccountMappingTemplate): IAccount[] {
        const flisOfGroups: IAccountMappingTemplateGroupItemFLI[] = [];
        const mappedAccounts: IAccount[] = [];

        if (template == null) {
            return mappedAccounts;
        }
        const templateGroups: IAccountMappingTemplateGroup[] = [template.group];
        const flis = this.getFlisOfGroups(templateGroups);
        flisOfGroups.push(...flis);

        flisOfGroups.forEach((fli: IAccountMappingTemplateGroupItemFLI) => {
            const accounts = this.getAccountsOfFli(fli.accounts);
            mappedAccounts.push(...accounts);
        });

        return mappedAccounts;
    }

    private getFlisOfGroups(templateGroups: IAccountMappingTemplateGroup[]): IAccountMappingTemplateGroupItemFLI[] {
        const flisOfGroups: IAccountMappingTemplateGroupItemFLI[] = [];
        for (const group of templateGroups) {
            const flisOfCurrentGroup: IAccountMappingTemplateGroupItemFLI[] = this.getFlisOfGroup(group.items);

            flisOfGroups.push(...flisOfCurrentGroup);

            if (group.groups && group.groups.length > 0) {
                const flis = this.getFlisOfGroups(group.groups);
                flisOfGroups.push(...flis);
            }
        }
        return flisOfGroups;
    }

    private getAccountsOfFli(accounts: AccountMappingAccountItem[]): IAccount[] {
        const mappedAccounts: IAccount[] = [];
        for (const account of accounts) {
            if (
                account.type !== ItemType.SUB_FLI &&
                account.type !== ItemType.EMPTY &&
                account.type !== ItemType.HEADER &&
                account.type !== ItemType.OF_THAT_ITEM
            ) {
                mappedAccounts.push(account as IAccount);
            } else if (account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) {
                mappedAccounts.push(...account.accounts);
            }
        }

        return mappedAccounts;
    }

    private getFlisOfGroup(itemsOfGroup: AccountMappingTemplateGroupItem[]): IAccountMappingTemplateGroupItemFLI[] {
        const flisOfItemsWithCorrectType = [];

        for (const item of itemsOfGroup) {
            if (item.type !== ItemType.SUB_TOTAL && item.type !== ItemType.HEADER && item.type !== ItemType.EMPTY) {
                flisOfItemsWithCorrectType.push(item as IAccountMappingTemplateGroupItemFLI);
            }
        }

        return flisOfItemsWithCorrectType;
    }

    private findGroupByName(
        groups: IAccountMappingTemplateGroup[],
        name: string,
    ): IAccountMappingTemplateGroup | null {
        for (const group of groups) {
            if (group.name === name) {
                return group;
            }
            if (group.groups?.length) {
                const found = this.findGroupByName(group.groups, name);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }
}
